import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface User {
  id: string;
  email: string;
  name: string;
  verified: boolean;
  created: string;
  updated: string;
}

interface AuthResponse {
  success: boolean;
  token: string;
  user: User;
  message?: string;
}

interface AuthState {
  // State
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
  
  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  initializeAuth: () => void;
  refreshAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        token: null,
        isLoading: false,
        error: null,
        isInitialized: false,
        
        // Actions
        login: async (email: string, password: string) => {
          set({ isLoading: true, error: null }, false, 'login:start');
          
          try {
            const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ email, password }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Login failed');
            }

            const authData: AuthResponse = await response.json();
            
            set(
              { 
                user: authData.user, 
                token: authData.token,
                isLoading: false, 
                error: null 
              },
              false,
              'login:success'
            );
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Login failed';
            set(
              { user: null, token: null, isLoading: false, error: message },
              false,
              'login:error'
            );
            throw error;
          }
        },
        
        register: async (email: string, password: string, name?: string) => {
          set({ isLoading: true, error: null }, false, 'register:start');
          
          try {
            const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ email, password, name }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || 'Registration failed');
            }

            const authData: AuthResponse = await response.json();
            
            set(
              { 
                user: authData.user, 
                token: authData.token,
                isLoading: false, 
                error: null 
              },
              false,
              'register:success'
            );
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Registration failed';
            set(
              { user: null, token: null, isLoading: false, error: message },
              false,
              'register:error'
            );
            throw error;
          }
        },
        
        logout: () => {
          set(
            { user: null, token: null, error: null, isLoading: false },
            false,
            'logout'
          );
        },
        
        clearError: () => {
          set({ error: null }, false, 'clearError');
        },
        
        initializeAuth: () => {
          console.log('Initializing auth with stored data...');
          const { user, token } = get();
          
          if (user && token) {
            console.log('Found stored auth data:', { userId: user.id, hasToken: !!token });
            // Validate token with backend
            get().refreshAuth();
          } else {
            console.log('No stored auth data found');
            set(
              { 
                user: null, 
                token: null,
                isInitialized: true,
                isLoading: false 
              },
              false,
              'initializeAuth:unauthenticated'
            );
          }
        },
        
        refreshAuth: async () => {
          const { token } = get();
          
          if (!token) {
            set({ user: null, token: null, isInitialized: true }, false, 'refreshAuth:no_token');
            return;
          }
          
          try {
            const response = await fetch(`${API_BASE_URL}/api/auth/validate`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });
            
            if (!response.ok) {
              const errorData = await response.json();
              console.log('Token validation failed:', errorData);
              
              // Clear invalid token
              get().logout();
              set({ isInitialized: true }, false, 'refreshAuth:invalid_token');
              return;
            }

            const validationData = await response.json();
            
            set({ 
              user: validationData.user, 
              error: null, 
              isInitialized: true 
            }, false, 'refreshAuth:success');
            
          } catch (error) {
            console.error('Token refresh failed:', error);
            // If refresh fails, user needs to login again
            get().logout();
            const message = error instanceof Error ? error.message : 'Session expired';
            set({ 
              error: message, 
              isInitialized: true 
            }, false, 'refreshAuth:error');
          }
        },
      }),
      {
        name: 'auth-store',
        // Persist user and token
        partialize: (state) => ({ 
          user: state.user, 
          token: state.token 
        }),
      }
    ),
    { name: 'auth-store' }
  )
);

// Auto-initialize auth on store creation
useAuthStore.getState().initializeAuth();
